import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Car, Cpu, Leaf, Smartphone, Fingerprint, ChevronRight } from "lucide-react";

const focusAreas = [
  {
    icon: Car, number: "01",
    title: "Materiali Innovativi per Interni Automotive",
    desc: "Soluzioni orientate all'impiego di materiali alternativi, al comfort e all'evoluzione dell'esperienza sensoriale a bordo.",
    details: ["Materiali bio-based e riciclabili", "Tessuti tecnici ad alte prestazioni", "Superfici tattili intelligenti", "Comfort termico e acustico avanzato"],
    color: "#FF3366",
    image: "https://images.unsplash.com/photo-1680446808444-07b70aca83cd?w=900&q=80",
    imageRight: "https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=600&q=80",
  },
  {
    icon: Cpu, number: "02",
    title: "Sistemi Intelligenti di Bordo",
    desc: "Tecnologie per l'evoluzione delle interfacce tra veicolo e conducente con funzionalita predittive e contestuali.",
    details: ["Dashboard AI predittiva", "Interfacce uomo-macchina next-gen", "Sistemi di assistenza contestuale", "Integrazione IoT veicolare"],
    color: "#3B82F6",
    image: "https://images.unsplash.com/photo-1562618817-253b06cf2b6e?w=900&q=80",
    imageRight: "https://images.unsplash.com/photo-1639060015191-9d83063eab2a?w=600&q=80",
  },
  {
    icon: Leaf, number: "03",
    title: "Gestione Intelligente delle Risorse",
    desc: "Sistemi progettati per migliorare l'efficienza e la sostenibilita di alcune funzionalita del veicolo.",
    details: ["Ottimizzazione energetica sistemi", "Gestione intelligente batteria", "Monitoraggio consumi real-time", "Algoritmi efficienza predittiva"],
    color: "#22C55E",
    image: "https://images.unsplash.com/photo-1593941707882-a5bba14938c7?w=900&q=80",
    imageRight: "https://images.unsplash.com/photo-1592318348310-f31b61a931c8?w=600&q=80",
  },
  {
    icon: Smartphone, number: "04",
    title: "Tecnologie per la Mobilita Digitale",
    desc: "Soluzioni innovative per semplificare processi e servizi connessi all'utilizzo dell'automobile.",
    details: ["Gestione veicolo da remoto", "Sistemi pagamento integrati", "Prenotazione manutenzione", "Piattaforme mobilita condivisa"],
    color: "#E97A1F",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=900&q=80",
    imageRight: "https://images.unsplash.com/photo-1585014165903-6d6c6ebad3e9?w=600&q=80",
  },
  {
    icon: Fingerprint, number: "05",
    title: "Identificazione e Gestione Digitale del Veicolo",
    desc: "Progetti orientati all'integrazione di nuove funzionalita digitali nell'ecosistema automotive.",
    details: ["Identita digitale su blockchain", "Accesso biometrico", "Certificazione storia veicolo", "Tracciabilita ciclo di vita"],
    color: "#A855F7",
    image: "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=900&q=80",
    imageRight: "https://images.unsplash.com/photo-1593941707874-ef25b8b4a92b?w=600&q=80",
  },
];

export default function InnovationLabPage() {
  return (
    <div className="bg-[#030308] min-h-screen" data-testid="innovation-lab-page">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#030308]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 flex items-center justify-between h-16 sm:h-20">
          <Link to="/" className="font-heading text-xl sm:text-2xl font-black tracking-tighter text-white hover:text-[#FF3366] transition-colors">NINFA</Link>
          <Link to="/" className="flex items-center gap-2 text-[#C0C0C0] hover:text-white transition-colors font-mono text-xs tracking-wider">
            <ArrowLeft size={16} /> HOME
          </Link>
        </div>
      </nav>

      {/* Hero with Video */}
      <section className="relative h-[70vh] sm:h-[85vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <video autoPlay muted loop playsInline className="w-full h-full object-cover opacity-35" poster="https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=1920&q=60">
            <source src="/innovation-lab-bg.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-[#030308] via-transparent to-[#030308]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#030308]/90 via-transparent to-[#030308]/60" />
        </div>
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)", backgroundSize: "60px 60px" }} />
        <div className="absolute top-1/4 left-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-[#FF3366]/8 rounded-full blur-[120px] animate-float pointer-events-none" />

        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 relative z-10 pt-16">
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}
            className="inline-block font-mono text-[10px] sm:text-xs tracking-[0.4em] text-[#FF3366] uppercase border border-[#FF3366]/30 px-3 py-1.5 backdrop-blur-sm bg-[#FF3366]/5">
            NINFA INNOVATION LAB
          </motion.span>
          <motion.h1 initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
            className="font-heading text-4xl sm:text-6xl lg:text-8xl font-bold tracking-tight mt-5 leading-[0.95]">
            Ricerca &<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF3366] via-[#A855F7] to-[#3B82F6]">Innovazione</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }}
            className="text-[#D0D0D0] font-body text-base sm:text-lg mt-5 max-w-xl leading-relaxed">
            Tecnologie avanzate per il settore automotive: sostenibilita, intelligenza artificiale e trasformazione digitale.
          </motion.p>
        </div>
      </section>

      {/* Focus Areas - Compact with images */}
      <section className="py-4 sm:py-8">
        {focusAreas.map((area, i) => (
          <motion.div
            key={i}
            data-testid={`focus-area-${i}`}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true, margin: "-50px" }}
          >
            <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[50vh] sm:min-h-[60vh]">
              {/* Image side */}
              <div className={`lg:col-span-5 relative overflow-hidden ${i % 2 === 0 ? "order-1" : "order-1 lg:order-2"}`}>
                <img src={area.image} alt={area.title} className="w-full h-full object-cover min-h-[200px] sm:min-h-[300px]" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#030308]/30 to-[#030308]/60 lg:bg-none" />
                {/* Small secondary image overlay */}
                <div className="absolute bottom-3 right-3 w-24 h-24 sm:w-36 sm:h-36 overflow-hidden border border-white/10">
                  <img src={area.imageRight} alt="" className="w-full h-full object-cover" loading="lazy" />
                </div>
                {/* Color accent */}
                <div className="absolute top-0 left-0 w-1 h-full" style={{ backgroundColor: area.color }} />
              </div>

              {/* Content side */}
              <div className={`lg:col-span-7 flex items-center ${i % 2 === 0 ? "order-2" : "order-2 lg:order-1"}`}>
                <div className="p-5 sm:p-8 lg:p-12 w-full">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="font-mono text-3xl sm:text-4xl font-black" style={{ color: `${area.color}40` }}>{area.number}</span>
                    <area.icon size={20} style={{ color: area.color }} />
                  </div>
                  <h2 className="font-heading text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight leading-tight text-white">
                    {area.title}
                  </h2>
                  <p className="text-[#C0C0C0] font-body text-sm sm:text-base mt-3 leading-relaxed max-w-lg">
                    {area.desc}
                  </p>
                  <div className="mt-4 sm:mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {area.details.map((d, j) => (
                      <div key={j} className="flex items-start gap-2 text-sm">
                        <ChevronRight size={14} style={{ color: area.color }} className="mt-0.5 flex-shrink-0" />
                        <span className="text-[#D0D0D0] font-body">{d}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            {/* Separator */}
            {i < focusAreas.length - 1 && <div className="h-px bg-white/5" />}
          </motion.div>
        ))}
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-16 border-t border-white/5 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#FF3366]/5 via-transparent to-[#3B82F6]/5 pointer-events-none" />
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 text-center relative z-10">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
            Collabora con il nostro <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF3366] to-[#3B82F6]">Innovation Lab</span>
          </h2>
          <a href="mailto:info@ninfa.eu" className="inline-block mt-6 bg-[#FF3366] text-white font-bold uppercase tracking-widest px-8 py-3.5 text-xs sm:text-sm hover:bg-white hover:text-black transition-colors duration-300">
            CONTATTACI
          </a>
        </div>
      </section>

      <footer className="bg-[#030308] border-t border-white/5 py-6">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <p className="text-[#B0B0B0] text-xs font-mono">&copy; {new Date().getFullYear()} NINFA.EU — P.IVA 18086141001</p>
        </div>
      </footer>
    </div>
  );
}
